import { BOARD_TF_OPTIONS } from './eodChartMath';
import { DEFAULT_LAYOUT_ID, TV_DATE_RANGES } from './chartLayouts';

export const BOARD_SETTINGS_KEY = 'tmr_board_settings';
export const BOARD_CACHE_PREFIX = 'tmr_board_cache_';

const DEFAULT_LAYOUT = {
  mainColPct: 59,
  subRowPct: 50,
  mainHeight: 340,
  subHeight: 200,
  bseHeight: 320,
  boardHeight: 620,
};

export function defaultBoardSettings() {
  return {
    showFib: true,
    tf: '5m',
    exch: 'NSE',
    period: '1M',
    /** TradingView multi-chart layout id (persisted until user changes). */
    layoutId: DEFAULT_LAYOUT_ID,
    /** Bottom date-range bar selection. */
    dateRangeId: '1M',
    rightPanelCollapsed: false,
    layout: { ...DEFAULT_LAYOUT },
  };
}

export function isValidLayoutId(id) {
  return typeof id === 'string' && id.length > 0;
}

export function isValidDateRangeId(id) {
  return TV_DATE_RANGES.some((r) => r.id === id);
}

function clampNum(n, min, max, fallback) {
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  return Math.min(max, Math.max(min, v));
}

function sanitizeLayout(layout) {
  const src = layout && typeof layout === 'object' ? layout : {};
  return {
    mainColPct: clampNum(src.mainColPct, 25, 80, DEFAULT_LAYOUT.mainColPct),
    subRowPct: clampNum(src.subRowPct, 20, 80, DEFAULT_LAYOUT.subRowPct),
    mainHeight: clampNum(src.mainHeight, 160, 900, DEFAULT_LAYOUT.mainHeight),
    subHeight: clampNum(src.subHeight, 120, 700, DEFAULT_LAYOUT.subHeight),
    bseHeight: clampNum(src.bseHeight, 160, 900, DEFAULT_LAYOUT.bseHeight),
    boardHeight: clampNum(src.boardHeight, 320, 1600, DEFAULT_LAYOUT.boardHeight),
  };
}

export function loadBoardSettings() {
  try {
    const raw = localStorage.getItem(BOARD_SETTINGS_KEY);
    if (!raw) return defaultBoardSettings();
    const parsed = JSON.parse(raw);
    const d = defaultBoardSettings();
    const exch = parsed.exch === 'BSE' || parsed.exch === 'NSE' ? parsed.exch : d.exch;
    const tf = isValidTf(parsed.tf) ? parsed.tf : d.tf;
    const layoutId = isValidLayoutId(parsed.layoutId) ? parsed.layoutId : d.layoutId;
    const dateRangeId = isValidDateRangeId(parsed.dateRangeId) ? parsed.dateRangeId : d.dateRangeId;
    return {
      ...d,
      ...parsed,
      exch,
      tf,
      layoutId,
      dateRangeId,
      rightPanelCollapsed: !!parsed.rightPanelCollapsed,
      layout: sanitizeLayout(parsed.layout),
    };
  } catch (_) {
    return defaultBoardSettings();
  }
}

export function saveBoardSettings(patch) {
  try {
    const prev = loadBoardSettings();
    const mergedLayout = { ...prev.layout, ...(patch.layout || {}) };
    const next = {
      ...prev,
      ...patch,
      exch: patch.exch === 'BSE' || patch.exch === 'NSE' ? patch.exch : prev.exch,
      tf: patch.tf && isValidTf(patch.tf) ? patch.tf : prev.tf,
      layoutId: patch.layoutId && isValidLayoutId(patch.layoutId) ? patch.layoutId : prev.layoutId,
      dateRangeId: patch.dateRangeId && isValidDateRangeId(patch.dateRangeId)
        ? patch.dateRangeId
        : prev.dateRangeId,
      layout: sanitizeLayout(mergedLayout),
    };
    localStorage.setItem(BOARD_SETTINGS_KEY, JSON.stringify(next));
    return next;
  } catch (_) {
    return null;
  }
}

export function isValidTf(tf) {
  return BOARD_TF_OPTIONS.some(o => o.value === tf);
}

/** Cache key — intraday uses today's date; EOD uses period range. */
export function boardCacheKey(exch, tf, from, to) {
  return `${BOARD_CACHE_PREFIX}${exch}_${tf}_${from || ''}_${to || ''}`;
}

export function loadBoardCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && Object.keys(parsed).length) return parsed;
  } catch (_) {}
  return null;
}

export function saveBoardCache(key, rawBySym) {
  try {
    sessionStorage.setItem(key, JSON.stringify(rawBySym));
  } catch (_) {}
}